// Where the viewer stands with the changelist GitHub App: the accounts it is
// installed on that the viewer can see, and from them the onboarding state
// the dashboard shows. Fetched by `installedAccounts` (api/install).
import type { Failure } from '../api/failure';

/**
 * What an installation reaches: nothing while it is suspended, every
 * repository of its account, or the selected ones the viewer can see
 * (`count`, possibly 0).
 */
export type Reach = { kind: 'suspended' } | { kind: 'all' } | { kind: 'selected'; count: number };

/**
 * Whose account it is, to the viewer:
 * - `own`: the viewer's personal account (fixed personal policy; they
 *   configure its installation);
 * - `user`: another user's personal account where the viewer collaborates
 *   (fixed personal policy, readable through one of its repositories,
 *   `repo`; nothing to configure);
 * - `organization`: governed by its organization policy. Admins configure
 *   the installation; members read the policy; anyone else (`outside`)
 *   reads it through one of its repositories, `repo`.
 * `repo` is null when no repository of the account could be named (a
 * suspended installation lists none).
 */
export type Owner =
  | { kind: 'own' }
  | { kind: 'user'; repo: string | null }
  | { kind: 'organization'; role: 'admin' | 'member' }
  | { kind: 'organization'; role: 'outside'; repo: string | null };

/** An account the App is installed on, as listed for the viewer. */
export interface InstalledAccount {
  status: 'listed';
  installationId: number;
  login: string;
  avatarUrl: string | null;
  reach: Reach;
  owner: Owner;
}

/** An account whose installation could not be looked into (its repositories or the viewer's membership). */
export interface FailedAccount {
  status: 'failed';
  installationId: number;
  login: string;
  avatarUrl: string | null;
  message: string;
}

export type AccountRow = InstalledAccount | FailedAccount;

/**
 * The dashboard's onboarding state:
 * - `not_installed`: the App is on no account the viewer can see;
 * - `no_repositories`: it is, but every installation reaches no repository
 *   (suspended, or no repository selected that the viewer can see);
 * - `ready`: at least one installation reaches a repository;
 * - `undetermined`: none is known to reach one, but an account could not
 *   be looked into, so neither of the other two can be said.
 * `accounts` is never empty, ordered as `ordered` orders it.
 */
export type Onboarding =
  | { state: 'not_installed' }
  | { state: 'no_repositories'; accounts: AccountRow[] }
  | { state: 'ready'; accounts: AccountRow[] }
  | { state: 'undetermined'; accounts: AccountRow[] };

/** True when the installation reaches at least one repository. */
export function reachesRepositories(reach: Reach): boolean {
  return reach.kind === 'all' || (reach.kind === 'selected' && reach.count > 0);
}

const isOwn = (a: AccountRow) => a.status === 'listed' && a.owner.kind === 'own';

/** The viewer's own account first, then the rest by login (`localeCompare` ignores case first). */
function ordered(accounts: AccountRow[]): AccountRow[] {
  return [...accounts].sort((a, b) => Number(isOwn(b)) - Number(isOwn(a)) || a.login.localeCompare(b.login));
}

export function onboarding(accounts: AccountRow[]): Onboarding {
  if (!accounts.length) return { state: 'not_installed' };
  const list = ordered(accounts);
  if (list.some((a) => a.status === 'listed' && reachesRepositories(a.reach))) return { state: 'ready', accounts: list };
  if (list.some((a) => a.status === 'failed')) return { state: 'undetermined', accounts: list };
  return { state: 'no_repositories', accounts: list };
}

/** The installation's settings on GitHub (where its repositories are chosen), when the viewer can configure it. */
export function configureUrl(account: InstalledAccount, webUrl: string): string | null {
  const base = webUrl.replace(/\/+$/, '');
  const id = encodeURIComponent(String(account.installationId));
  const o = account.owner;
  if (o.kind === 'own') return `${base}/settings/installations/${id}`;
  if (o.kind === 'organization' && o.role === 'admin')
    return `${base}/organizations/${encodeURIComponent(account.login)}/settings/installations/${id}`;
  return null;
}

/**
 * The account's policy page, naming a repository the viewer can read where
 * neither ownership nor membership lets them read it; null when there is
 * none to name.
 */
export function policyHref(account: InstalledAccount): string | null {
  const page = `/settings/orgs/${encodeURIComponent(account.login)}`;
  const o = account.owner;
  if (o.kind === 'own' || (o.kind === 'organization' && o.role !== 'outside')) return page;
  return o.repo === null ? null : `${page}?repo=${encodeURIComponent(o.repo)}`;
}

/**
 * The dashboard's view of the App's installations: still loading, not
 * listable with this sign-in (a token that is not an App user token),
 * failed, or known.
 */
export type AccountsView =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | { status: 'error'; failure: Failure }
  | { status: 'known'; onboarding: Onboarding };
